import DatePicker from '@/components/features/shared/DatePicker'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { CreateAgreementData } from '@/domain/agreements/agreement'
import { Collector } from '@/domain/collectors/collector'
import { formatBillingFrequencyOnLabel, Offering, ServiceType } from '@/domain/offerings/offering'
import { SellCode } from '@/domain/parcels/parcel'
import { convertToDigitStr } from '@/utils/helpers'
import { addMonths } from 'date-fns'
import { AlertCircleIcon, Check, CreditCard, DollarSign, User, Wallet } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { renderSinglePeriod } from '../../shared/utils'
import { installmentsOptions } from '@/constants/installmentOptions'

interface AgreementDetailsFormProps {
  form: ReturnType<typeof useForm<CreateAgreementData>>
  serviceType: ServiceType
  offering?: Offering
  collectors?: Collector[]
  sellers?: Collector[]
}

export const AgreementDetailsForm = ({
  form,
  serviceType,
  offering,
  collectors,
  sellers,
}: AgreementDetailsFormProps) => {
  const startDate = form.watch('startDate')
  const hasPaymentPlanAvailable = offering?.hasPaymentPlanAvailable ?? false
  const requiresAdvancePayment = hasPaymentPlanAvailable && serviceType === ServiceType.ABONOS
  const amount = form.watch('amount')
  const initialAmount = form.watch('initialAmount')
  const installmentAmount = form.watch('installmentAmount')
  const installments = form.watch('installments') || undefined
  const installmentsValue = installmentAmount && installments ? installmentAmount / installments : 0
  const sellerId = form.watch('sellerId')

  const [showInitialAmount, setShowInitialAmount] = useState((initialAmount || 0) > 0)
  const [paymentMethod, setPaymentMethod] = useState<'full' | 'plan'>('full');

  useEffect(() => {
    if (paymentMethod === 'full') {
      form.setValue('hasPaymentPlan', false);
      form.setValue('initialAmount', undefined);
      form.setValue('installments', 1)
      form.setValue('installmentAmount', 0)
      setShowInitialAmount(false);
    } else {
      form.setValue('hasPaymentPlan', true);
      if (!form.getValues('installments')) {
        form.setValue('installments', 3);
        form.setValue('installmentAmount', amount);
      }
    }
  }, [paymentMethod]);

  useEffect(() => {
    if (requiresAdvancePayment) {
      form.setValue('hasPaymentPlan', true);
    } else {
      form.setValue('hasPaymentPlan', false);
    }
  }, [requiresAdvancePayment]);

  const totalInstallmentAmount = (initialAmount || 0) + (installmentAmount || 0)

  return (
    <div className="space-y-6">
      {/* General Contract Details */}
      <Card className="border-gray-200">
        <CardHeader className="pb-3 border-b">
          <CardTitle className="text-lg">Detalles del Contrato</CardTitle>
        </CardHeader>
        <CardContent className="pt-4 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Seller Selection */}
            <div className="space-y-2">
              <label className="text-sm font-medium flex items-center gap-2">
                {/* <User className="h-4 w-4" /> */}
                Vendedor
              </label>
              <Select
                onValueChange={(value: string) => {
                  form.setValue('sellerId', value)
                }}
                value={sellerId}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar vendedor (opcional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Sin vendedor asignado</SelectItem>
                  {sellers?.map((seller) => (
                    <SelectItem key={seller.id} value={seller.id}>
                      {seller.name} ({seller.code})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {form.formState.errors.sellerId && (
                <p className="text-sm text-red-500">{form.formState.errors.sellerId?.message}</p>
              )}
            </div>

            {/* Collector Selection */}
            <div className="space-y-2">
              <label className="text-sm font-medium flex items-center gap-2">
                Cobrador
              </label>
              <Select
                onValueChange={(value: string) => {
                  form.setValue('collectorId', value)
                }}
                value={form.watch('collectorId')}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar cobrador (opcional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Sin cobrador asignado</SelectItem>
                  {collectors?.map((collector) => (
                    <SelectItem key={collector.id} value={collector.id}>
                      {collector.name} ({collector.code})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {form.formState.errors.collectorId && (
                <p className="text-sm text-red-500">{form.formState.errors.collectorId?.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Fecha de Inicio del contrato</label>
              <DatePicker
                date={form.watch('startDate')}
                onSelect={(date: Date) => { form.setValue('startDate', date) }}
              />
              {form.formState.errors.startDate && (
                <p className="text-sm text-red-500">{form.formState.errors.startDate?.message}</p>
              )}
            </div>

            {serviceType === ServiceType.CREMACIONES && (
              <div className="space-y-2">
                <label className="text-sm font-medium">Fecha programada de cremación</label>
                <DatePicker
                  date={form.watch('endDate')}
                  onSelect={(date: Date) => { form.setValue('endDate', date) }}
                />
                {form.formState.errors.endDate && (
                  <p className="text-sm text-red-500">{form.formState.errors.endDate?.message}</p>
                )}
              </div>
            )}

            {(serviceType === ServiceType.CREMACIONES) && (
              <div className="space-y-2">
                <label className="text-sm font-medium">Tipo de Venta</label>
                <Select
                  onValueChange={(value: SellCode) => form.setValue('sellCode', value)}
                  value={form.watch('sellCode')}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccione un plan" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={SellCode.URGENCY}>Urgencia</SelectItem>
                    <SelectItem value={SellCode.PRENECESSITY}>Prenecesidad</SelectItem>
                    <SelectItem value={SellCode.TRANSFER}>Traslado</SelectItem>
                  </SelectContent>
                </Select>
                {form.formState.errors.sellCode && (
                  <p className="text-sm text-red-500">{form.formState.errors.sellCode?.message}</p>
                )}
              </div>
            )}

            {(serviceType === ServiceType.CREMACIONES) && (
              <div className="space-y-2">
                <label className="text-sm font-medium">Número de Contrato</label>
                <Input
                  placeholder="Auto-generado"
                  {...form.register('contractNumber')}
                />
                {form.formState.errors.contractNumber && (
                  <p className="text-sm text-red-500">{form.formState.errors.contractNumber?.message}</p>
                )}
              </div>
            )}

            {(serviceType === ServiceType.CREMACIONES) && (
              <div className="space-y-2">
                <label className="text-sm font-medium">Monto final</label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                  <Input
                    disabled={paymentMethod === 'plan'}
                    type="number"
                    step="1"
                    placeholder="0.00"
                    className={`pl-10 text-lg font-bold`}
                    {...form.register('amount', {
                      valueAsNumber: true,
                      onChange: (e) => {
                        const value = parseFloat(e.target.value) || 0;
                        form.setValue('amount', value, { shouldValidate: true });
                      }
                    })}
                    onWheel={(e) => e.currentTarget.blur()}
                  />
                </div>
              </div>
            )}

            {serviceType === ServiceType.ABONOS && (
              <>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Período de inicio del abono</label>
                  <div className="flex flex-row items-center justify-start gap-5 mt-1">
                    <span className={`capitalize font-mono font-medium text-gray-900 rounded-xl border border-gray-200 px-2 py-1 bg-gray-50`}>
                      {requiresAdvancePayment ? renderSinglePeriod(addMonths(startDate, 1), '/') : renderSinglePeriod(startDate, '/')}
                    </span>
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Monto final {formatBillingFrequencyOnLabel(offering?.billingFrequency)}</label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                    <Input
                      disabled={serviceType === ServiceType.ABONOS}
                      type="number"
                      step="1"
                      placeholder="0.00"
                      className={`pl-10 text-lg font-bold`}
                      {...form.register('amount', {
                        valueAsNumber: true,
                        onChange: (e) => {
                          const value = parseFloat(e.target.value) || 0;
                          form.setValue('amount', value, { shouldValidate: true });
                        }
                      })}
                      onWheel={(e) => e.currentTarget.blur()}
                    />
                  </div>
                </div>
                <div className="space-y-2 self-end">
                  <div className="p-2 flex items-center gap-2 bg-green-50 border border-green-100 rounded-xl animate-in fade-in slide-in-from-top-1">
                    <Check className="h-4 w-4 text-green-500 flex-shrink-0" />
                    <p className="text-green-800 text-sm flex items-center gap-2 font-medium">
                      <span>
                        El monto final {formatBillingFrequencyOnLabel(offering?.billingFrequency)} será determinado en base a la edad del integrante de mayor edad
                      </span>
                    </p>
                  </div>
                </div>
                {requiresAdvancePayment && <div className="col-span-2">
                  <div className="p-4 bg-red-50 border border-red-100 rounded-xl animate-in fade-in slide-in-from-top-1">
                    <p className="text-red-800 text-sm flex items-center gap-2 font-medium">
                      <AlertCircleIcon className="h-4 w-4 text-red-500 flex-shrink-0" />
                      <span>
                        Esta contratación requiere un monto de anticipo de servicio. <br />
                      </span>
                    </p>
                  </div>
                </div>}
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {hasPaymentPlanAvailable && (
        <Card className="border-gray-200">
          <CardHeader className="pb-3 border-b">
            <div className="flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-gray-400" />
              <CardTitle className="text-lg">Método de Pago</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="pt-4 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" role="radiogroup" aria-label="Método de pago">
              <div
                role="radio"
                aria-checked={paymentMethod === 'full'}
                tabIndex={0}
                onClick={() => setPaymentMethod('full')}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setPaymentMethod('full');
                  }
                }}
                className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${paymentMethod === 'full' ? 'border-blue-500 bg-blue-50' : 'border-gray-100 hover:border-gray-200'}`}
              >
                <div className={`p-2 rounded-full ${paymentMethod === 'full' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-500'}`}>
                  <Wallet className="h-5 w-5" />
                </div>
                <div>
                  <p className="font-bold">Pago Total</p>
                  <p className="text-xs text-muted-foreground">Abonar el 100% al contado</p>
                </div>
              </div>
              <div
                role="radio"
                aria-checked={paymentMethod === 'plan'}
                tabIndex={0}
                onClick={() => setPaymentMethod('plan')}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setPaymentMethod('plan');
                  }
                }}
                className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${paymentMethod === 'plan' ? 'border-blue-500 bg-blue-50' : 'border-gray-100 hover:border-gray-200'}`}
              >
                <div className={`p-2 rounded-full ${paymentMethod === 'plan' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-500'}`}>
                  <CreditCard className="h-5 w-5" />
                </div>
                <div>
                  <p className="font-bold">Plan de Pago</p>
                  <p className="text-xs text-muted-foreground">Financiar en cuotas</p>
                </div>
              </div>
            </div>

            {paymentMethod === 'full' && serviceType === ServiceType.ABONOS && <div className="space-y-2">
              <label className="text-sm font-medium">{requiresAdvancePayment ? 'Monto de Anticipo de servicio' : 'Monto de anticipo'}</label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input
                  type="number"
                  step="1"
                  placeholder="0.00"
                  className={`pl-10 text-lg font-bold`}
                  {...form.register('installmentAmount', {
                    valueAsNumber: true,
                  })}
                  onWheel={(e) => e.currentTarget.blur()}
                />
              </div>
              {form.formState.errors.installmentAmount && <p className="text-red-500 text-sm">{form.formState.errors.installmentAmount.message}</p>}
            </div>}

            {!requiresAdvancePayment && paymentMethod !== 'plan' && (amount || 0) > 0 && (
              <div className="p-4 bg-green-50 border border-green-100 rounded-xl animate-in fade-in slide-in-from-top-1">
                <p className="text-green-800 text-sm flex items-center gap-2 font-medium">
                  <Wallet className="h-4 w-4" />
                  <span>Se generará un cobro total por <strong>${convertToDigitStr(amount?.toString() || '0')}</strong> abonado en un solo pago.</span>
                </p>
              </div>
            )}

            {requiresAdvancePayment && paymentMethod === 'full' && (totalInstallmentAmount || 0) > 0 && (
              <div className="p-4 bg-green-50 border border-green-100 rounded-xl animate-in fade-in slide-in-from-top-1">
                <p className="text-green-800 text-sm flex items-center gap-2 font-medium">
                  <Wallet className="h-4 w-4" />
                  <span>Se generará un cobro total por <strong>${convertToDigitStr(totalInstallmentAmount?.toString() || '0')}</strong> abonado en un solo pago.</span>
                </p>
              </div>
            )}

            {paymentMethod === 'plan' && (
              <div className="space-y-4 pt-4 border-t border-gray-100 animate-in fade-in slide-in-from-top-1">
                <div className="flex items-center space-x-2 mb-2">
                  <Switch
                    id="has-initial-extra"
                    checked={showInitialAmount}
                    onCheckedChange={(checked) => {
                      setShowInitialAmount(checked)
                      if (!checked) form.setValue('initialAmount', 0)
                    }}
                  />
                  <label htmlFor="has-initial-extra" className="text-sm font-medium cursor-pointer">
                    ¿Posee un monto de entrega inicial?
                  </label>
                </div>

                {showInitialAmount && (
                  <div className="space-y-2 max-w-xs animate-in fade-in zoom-in duration-200">
                    <label className="text-sm font-medium">Monto de Entrega Inicial</label>
                    <div className="relative">
                      <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                      <Input
                        type="number"
                        step="1"
                        placeholder="0.00"
                        className="pl-10 text-lg font-bold"
                        {...form.register('initialAmount', {
                          valueAsNumber: true,
                          onChange: (e) => {
                            const value = parseFloat(e.target.value) || 0;
                            form.setValue('initialAmount', value, { shouldValidate: true });
                          }
                        })}
                        onWheel={(e) => e.currentTarget.blur()}
                      />
                    </div>
                    {form.formState.errors.initialAmount && (
                      <p className="text-sm text-red-500">{form.formState.errors.initialAmount?.message}</p>
                    )}
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">{requiresAdvancePayment ? 'Monto de Anticipo de servicio' : 'Monto a financiar'}</label>
                    <div className="relative">
                      <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                      <Input
                        type="number"
                        step="1"
                        placeholder="0.00"
                        className="pl-10 text-lg font-bold"
                        {...form.register('installmentAmount', {
                          valueAsNumber: true,
                          onChange: (e) => {
                            const value = parseFloat(e.target.value) || 0;
                            form.setValue('installmentAmount', value, { shouldValidate: true });
                          }
                        })}
                        onWheel={(e) => e.currentTarget.blur()}
                      />
                    </div>
                    {form.formState.errors.installmentAmount && (
                      <p className="text-sm text-red-500">{form.formState.errors.installmentAmount?.message}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Cuotas</label>
                    <Select
                      onValueChange={(value) => form.setValue('installments', Number(value))}
                      value={form.watch('installments')?.toString()}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Cuotas" />
                      </SelectTrigger>
                      <SelectContent>
                        {installmentsOptions.map((n) => (
                          <SelectItem key={n} value={n.toString()}>
                            {n} cuotas
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {form.formState.errors.installments && (
                      <p className="text-sm text-red-500">{form.formState.errors.installments?.message}</p>
                    )}
                  </div>
                </div>

                {installmentsValue > 0 && (
                  <div className="p-4 bg-green-50 border border-green-100 rounded-xl animate-in fade-in slide-in-from-top-1">
                    <p className="text-green-800 text-sm flex items-center gap-2 font-medium">
                      <Wallet className="h-4 w-4" />
                      <span>
                        Se financiará el monto de anticipo de <strong>${convertToDigitStr((installmentAmount || 0).toString())}</strong> en <strong>{(installments || 0)} cuotas</strong> de <strong>${convertToDigitStr(installmentsValue.toString())}</strong>.
                        {(initialAmount || 0) > 0 && <br />}
                        {(initialAmount || 0) > 0 &&
                          <>Además, se hará una entrega inicial de <strong>${convertToDigitStr(initialAmount?.toString())}</strong></>}
                      </span>
                    </p>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {!hasPaymentPlanAvailable && serviceType !== ServiceType.ABONOS && (
        <Card className="border-gray-200">
          <CardHeader className="pb-3 border-b">
            <CardTitle className="text-lg">Monto de la Contratación</CardTitle>
          </CardHeader>
          <CardContent className="pt-4 grid grid-cols-2 items-center gap-4">
            <div className="space-y-2 max-w-xs">
              <label className="text-sm font-medium">Monto Final {formatBillingFrequencyOnLabel(offering?.billingFrequency)}</label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input
                  type="number"
                  step="1"
                  placeholder="0.00"
                  className="pl-10"
                  {...form.register('amount', {
                    valueAsNumber: true,
                    onChange: (e) => {
                      const value = parseFloat(e.target.value) || 0;
                      form.setValue('amount', value, { shouldValidate: true });
                    }
                  })}
                  onWheel={(e) => e.currentTarget.blur()}
                />
              </div>
              {form.formState.errors.amount && (
                <p className="text-sm text-red-500">{form.formState.errors.amount?.message}</p>
              )}
            </div>
            {amount > 0 && <div className="self-end p-2 flex items-center gap-2 bg-green-50 border border-green-100 rounded-xl animate-in fade-in slide-in-from-top-1">
              <Check className="h-4 w-4 text-green-500 flex-shrink-0" />
              <p className="text-green-800 text-sm flex items-center gap-2 font-medium">
                <span>
                  Se cobrará un monto final de de ${convertToDigitStr(amount)}
                </span>
              </p>
            </div>}
          </CardContent>
        </Card>
      )}
    </div>
  )
}